import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, X } from 'lucide-react';
import AdminModuleUnlockModal from '../components/AdminModuleUnlockModal';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

// Dezentes tracked Label mit Gold-Punkt
const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-semibold text-sand-600" style={{ letterSpacing: '0.18em' }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
    {children}
  </span>
);

const CATEGORIES = [
  { key: 'all', label: 'Alle Kategorien' },
  { key: 'certificate', label: 'Mit Zertifikat' },
  { key: 'exam', label: 'Mit Prüfung' },
  { key: 'learning', label: 'Lernsystem' },
];

const AdminCoursesPage = ({ Layout }) => {
  const { getAuthHeader } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [unlockCourse, setUnlockCourse] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadCourses(); }, []);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    let base = courses;
    if (category === 'certificate') base = base.filter((c) => c.has_certificate);
    else if (category === 'exam') base = base.filter((c) => c.requires_exam);
    else if (category === 'learning') base = base.filter((c) => c.learning_system);
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter((c) => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [courses, search, category]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade-in" data-testid="admin-courses-page">
        {/* Kopf */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">Administration · Kurse</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-[1.05]">
            Kurs<span className="italic text-terra">verwaltung</span>.
          </h1>
        </div>

        {/* Suche + Kategorie */}
        <div className="flex flex-wrap items-center gap-3 mb-7">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-sand-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kurs suchen…"
              className="w-full pl-11 pr-9 h-[46px] bg-cream-100 border border-cream-300 rounded-2xl text-[14.5px] text-sand-800 placeholder:text-sand-400 focus:outline-none focus:border-terra"
              data-testid="course-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-sand-500 hover:text-sand-800">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-[46px] px-3 bg-cream-100 border border-cream-300 rounded-2xl text-sm text-sand-800 focus:outline-none focus:border-terra"
            data-testid="course-category-select"
          >
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span className="font-mono text-[10.5px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.16em' }} data-testid="course-count">
            {filtered.length} Kurse
          </span>
        </div>

        {/* Karten */}
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((course) => (
            <div
              key={course.id}
              className="group bg-cream-100 rounded-3xl border border-cream-200 p-6 shadow-soft hover:shadow-hover hover:border-cream-300 hover:-translate-y-0.5 transition-all flex flex-col"
              data-testid={`admin-course-${course.id}`}
            >
              {/* Labels */}
              <div className="flex gap-4 flex-wrap mb-3">
                {course.has_certificate && <Tag>Zertifikat</Tag>}
                {course.requires_exam && <Tag>Prüfung</Tag>}
                {course.learning_system && <Tag>Lernsystem</Tag>}
              </div>

              <h3 className="font-display text-[21px] text-espresso mb-2 leading-snug">{course.title}</h3>
              <p className="text-sm text-sand-600 mb-4 line-clamp-2 leading-relaxed">{course.description}</p>

              <div className="flex items-center gap-4 font-mono text-[10px] uppercase font-semibold text-sand-500 mb-5 mt-auto" style={{ letterSpacing: '0.16em' }}>
                <span>{course.total_modules} Module</span>
                <span style={{ color: GOLD_INK }}>·</span>
                <span>{course.total_ue} UE</span>
              </div>

              {/* EIN primärer Button + Textlinks */}
              <div className="pt-4 border-t border-cream-200">
                <button
                  onClick={() => navigate(`/admin/participants/${course.id}`)}
                  className="w-full h-[42px] rounded-full text-[12px] font-bold uppercase transition-colors"
                  style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.16em' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TERRA; e.currentTarget.style.color = '#F5F0EA'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TERRA; }}
                  data-testid={`open-course-${course.id}`}
                >
                  Öffnen
                </button>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <button
                    onClick={() => navigate(`/admin/users?view=course:${course.id}`)}
                    className="text-[12.5px] font-medium text-sand-600 hover:text-terra transition-colors"
                    data-testid={`participants-btn-${course.id}`}
                  >
                    Teilnehmer
                  </button>
                  <button
                    onClick={() => navigate(`/course/${course.id}`)}
                    className="text-[12.5px] font-medium text-sand-600 hover:text-terra transition-colors"
                    data-testid={`preview-btn-${course.id}`}
                  >
                    Vorschau
                  </button>
                  <button
                    onClick={() => setUnlockCourse(course)}
                    className="text-[12.5px] font-medium text-sand-600 hover:text-terra transition-colors"
                    data-testid={`unlock-btn-${course.id}`}
                  >
                    Freischalten
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="font-display italic text-[18px] text-sand-600 text-center py-12">
            Keine Kurse gefunden — prüfe die Suche oder Kategorie.
          </p>
        )}

        {unlockCourse && (
          <AdminModuleUnlockModal
            courseId={unlockCourse.id}
            courseTitle={unlockCourse.title}
            getAuthHeader={getAuthHeader}
            onClose={() => setUnlockCourse(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default AdminCoursesPage;
